import { OverwatchUnit } from './types';

export const OVERWATCH_EMPTY_VALUE = '—';

export interface FormatOverwatchValueOptions {
  /** ISO 4217 code from the envelope; required for `currency`. */
  currency?: string;
  /** Up to two decimals by default, trailing zeros trimmed. */
  maximumFractionDigits?: number;
  locale?: string;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

const formatDuration = (totalSeconds: number): string => {
  const seconds = Math.round(totalSeconds);

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts = [
    days ? `${days}d` : null,
    hours ? `${hours}h` : null,
    minutes ? `${minutes}m` : null
  ].filter(Boolean);

  return parts.slice(0, 2).join(' ');
};

/** Up to `digits` decimals, but a value short of 100 never rounds up to it. */
const capBelowHundred = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  const rounded = Math.round(value * factor) / factor;

  return value < 100 && rounded >= 100 ? Math.floor(value * factor) / factor : rounded;
};

/**
 * A raw API number and its contract unit as display text. Percentages keep up to two decimals
 * (99.95%, 68.4%, 100%); currency under 1,000 keeps exactly two ($0.18), above it is compact
 * ($1.84M).
 */
export const formatOverwatchValue = (
  value: number | null | undefined,
  unit: OverwatchUnit | undefined,
  { currency, maximumFractionDigits = 2, locale = 'en-US' }: FormatOverwatchValueOptions = {}
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return OVERWATCH_EMPTY_VALUE;
  }

  const number = (options: Intl.NumberFormatOptions = {}, input: number = value) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits, ...options }).format(input);

  switch (unit) {
    case 'percent':
      return `${number({}, capBelowHundred(value, maximumFractionDigits))}%`;
    case 'percent_change':
      return `${number({ signDisplay: 'exceptZero' })}%`;
    case 'percentage_points':
      return `${number({ signDisplay: 'exceptZero' })} pp`;
    case 'ratio':
      return `${number({ minimumFractionDigits: 1 })}x`;
    case 'currency': {
      const style: Intl.NumberFormatOptions = currency ? { style: 'currency', currency } : {};

      return Math.abs(value) < 1000
        ? number({ ...style, minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : number({
            ...style,
            notation: 'compact',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          });
    }
    case 'duration_seconds':
      return formatDuration(value);
    case 'bytes': {
      let scaled = value;
      let index = 0;

      while (Math.abs(scaled) >= 1024 && index < BYTE_UNITS.length - 1) {
        scaled /= 1024;
        index += 1;
      }

      return `${new Intl.NumberFormat(locale, { maximumFractionDigits }).format(scaled)} ${
        BYTE_UNITS[index]
      }`;
    }
    default:
      return number();
  }
};
